import { addItemToCart, createCart } from "@/api/saleor/services/checkout";
import { getCartIdByLocale } from "@/apiUtils/commonUtils";
import {
  atpCheckExcludedProducts,
  cartErrors,
  MetaDataKeys,
  SaleorAttributes,
  siteIds,
} from "@/apiUtils/constants";
import {
  getFromCookieMetadataSA,
  setInCookieMetadataSA,
} from "@/apiUtils/cookieDataUtilsServerActions";
import {
  getChannelIdClient,
  getCountryAlpha3CodeFromLocale,
  getCountryCodeFromLocale,
  getLangFromLocale,
} from "./common";
import { useGlobalStore } from "@/store/store";
import {
  AtpCheckResponses,
  AttributesTypes,
  AvailableGifts,
  CheckoutLines,
  RealProductInventoryType,
} from "@/common/lib/types";
import { getRealtimeInventoryDetails } from "@/api/omniChannel";
import { getAlgoliaIndexName } from "@/apiUtils/algoliaUtils";
import { algoliaAddToCartWithQueryClient } from "@/apiUtils/algoliaUtils/indexClient";
import { algoliaEventParam, eventParam } from "@/apiUtils/algoliaUtils/constants";
import { analyticEvents, handlePostAnalytics } from "@/apiUtils/analyticsUtils";
import { LanguageCodeEnum } from "@/api/saleor/generated/graphql";

export const syncCartVariantIds = (lines: CheckoutLines[]) => {
  if (!lines) return;
  const variantIds = lines.map((line) => line?.variant?.id).filter(Boolean);
  useGlobalStore.getState().updateCartVariantIds(variantIds);
};

export const addNewCartWithMultiVariantSupport = async (
  locale: string,
  listItems: { variantId: string; quantity: number; metadata?: { key: string; value: string }[] }[],
) => {
  const channel = getChannelIdClient(locale as string);
  console.log({ channel });
  const lang = getLangFromLocale(locale);
  const languageCode = lang === "ar" ? LanguageCodeEnum.Ar : LanguageCodeEnum.En;
  const response = await createCart(channel, languageCode, listItems);
  const id = response?.checkoutCreate?.checkout?.id;
  if (id) {
    await setInCookieMetadataSA(getCartIdByLocale(locale), id).catch((error) => console.error("Failed to save cart id cookie", error));
  }
  return response?.checkoutCreate || null;
};

export const addNewCart = async (
  locale: string,
  variantId: string,
  metadata?: { key: string; value: string }[],
) => {
  const channel = getChannelIdClient(locale as string);
  console.log({ channel });
  const lang = getLangFromLocale(locale);
  const languageCode = lang === "ar" ? LanguageCodeEnum.Ar : LanguageCodeEnum.En;
  const response = await createCart(channel, languageCode, [
    { variantId, quantity: 1, ...(metadata ? { metadata } : {}) },
  ]);
  const id = response?.checkoutCreate?.checkout?.id;
  if (id) {
    await setInCookieMetadataSA(getCartIdByLocale(locale), id).catch((error) => console.error("Failed to save cart id cookie", error));
  }
  return response?.checkoutCreate || null;
};

export const addToCartById = async (
  locale: string,
  variantId: string,
  additional: { magrabiId?: string; indexName?: string; objectID?: string; queryID?: string },
) => {
  const cartId = (await getFromCookieMetadataSA(getCartIdByLocale(locale))) ?? "";
  let response;
  const metadata = additional?.queryID
    ? [{ key: MetaDataKeys.QUERY_ID, value: additional.queryID }]
    : undefined;

  if (!cartId) {
    response = await addNewCart(locale, variantId, metadata);
  } else {
    response = await addItemToCart(cartId, { id: variantId, quantity: 1 }, metadata);
    console.log("add to cart response-->", cartId, response);
    const [error] = response?.errors || [];
    if (error?.code === cartErrors.NOTFOUND) {
      response = await addNewCart(locale, variantId, metadata);
    }
  }

  if (response?.checkout) {
    const indexName = getAlgoliaIndexName("", locale);
    algoliaAddToCartWithQueryClient(
      additional?.indexName || indexName,
      eventParam?.ADDED_TO_CART,
      additional?.objectID || variantId,
      additional?.queryID,
      additional?.magrabiId,
    );

    syncCartVariantIds(response.checkout.lines);
    useGlobalStore.getState().setCartUpdatedAt(Date.now());
  }
  return response;
};

export const getUpdateRealtimeInventory = async (
  siteId: string,
  productIds: string[],
  lines: CheckoutLines[],
): Promise<CheckoutLines[]> => {
  try {
    const response: AtpCheckResponses | undefined = await getRealtimeInventoryDetails(
      productIds,
      siteId,
    );
    const inventoryHashmap = response?.productIds?.reduce(
      (acc, item: RealProductInventoryType) => {
        acc[item?.productId] = item.stock;
        return acc;
      },
      {} as Record<string, number>,
    );

    const updatedLines = lines.map((line) => {
      const { variant } = line;
      const { sku, quantityAvailable } = variant;
      const availableQuantityInMacula = inventoryHashmap?.[sku] ?? 0;
      const actualStockAvailable = availableQuantityInMacula
        ? Math.min(availableQuantityInMacula, quantityAvailable!)
        : 0;
      return {
        ...line,
        variant: {
          ...line.variant,
          quantityAvailable: actualStockAvailable,
        },
      };
    });
    return updatedLines;
  } catch (error) {
    console.error(error);
    // Never wipe cart rows on ATP failure — order summary still has checkout data.
    return lines;
  }
};

export const getInventoryLines = async (lines: CheckoutLines[], locale: string) => {
  const country = getCountryCodeFromLocale(locale) || "";
  const siteId = siteIds?.[country] || "";
  const productIds = Array.isArray(lines)
    ? lines
        .filter((line) => !atpCheckExcludedProducts.has(line?.variant?.product?.productType?.slug))
        .map((line) => `${line?.variant?.sku}`)
    : [];
  return getUpdateRealtimeInventory(siteId, productIds, lines);
};

export type AttributeResult = {
  translation: string | null;
  value: string | null;
};

export function getAttributeValue(
  data: AttributesTypes[],
  lang: string,
  slug: SaleorAttributes,
): AttributeResult {
  // Find the attribute object that matches the given slug
  const attribute = data?.find((item) => item?.attribute?.slug === slug);

  if (!attribute) {
    return { translation: null, value: null };
  }

  // Get the translation based on the language
  const translation =
    lang === "ar"
      ? attribute?.attribute?.translation?.name || attribute?.attribute?.name
      : attribute?.attribute?.name;

  // Get the value based on the inputType and language
  const value = getValueBasedOnInputType(attribute, lang);

  return { translation, value };
}

function getValueBasedOnInputType(attribute: AttributesTypes, lang: string): string | null {
  const valueObj = attribute?.values[0]; // Assuming there's only one value per attribute
  const brandId = `${process.env.NEXT_PUBLIC_BRAND_ID ?? ""}`;

  switch (attribute?.attribute?.inputType) {
    case "SCOPPABLE":
      return lang === "ar"
        ? valueObj?.translation?.scoppable
          ? JSON.parse(valueObj?.translation?.scoppable)?.[brandId] ||
            JSON.parse(valueObj?.translation?.scoppable)?.magrabi
          : valueObj?.scoppable
            ? JSON.parse(valueObj?.scoppable)?.[brandId] || JSON.parse(valueObj?.scoppable)?.magrabi
            : null
        : valueObj?.scoppable
          ? JSON.parse(valueObj?.scoppable)?.[brandId] || JSON.parse(valueObj?.scoppable)?.magrabi
          : null;

    case "BOOLEAN":
      return valueObj?.boolean !== null ? valueObj?.boolean?.toString() : null;

    case "PLAIN_TEXT":
      return lang === "ar"
        ? valueObj?.translation?.plainText || valueObj?.plainText
        : valueObj?.plainText;

    case "REFERENCE_ENTITY":
      return lang === "ar"
        ? valueObj?.translation?.referenceEntity
          ? JSON.parse(valueObj?.translation?.referenceEntity)?.attributes?.label
          : valueObj?.referenceEntity
            ? JSON.parse(valueObj?.referenceEntity)?.attributes?.label
            : null
        : valueObj?.referenceEntity
          ? JSON.parse(valueObj?.referenceEntity)?.attributes?.label
          : null;

    case "DROPDOWN":
      return lang === "ar" ? valueObj?.translation?.name || valueObj?.name : valueObj?.name;

    default:
      return null;
  }
}

export function getAttributeKeyValuePairs(
  data: AttributesTypes[],
  lang: string,
  slugs: SaleorAttributes[],
): Record<string, AttributeResult> {
  const result: Record<string, AttributeResult> = {};

  slugs.forEach((slug) => {
    result[slug] = getAttributeValue(data, lang, slug);
  });

  return result;
}

export function parseLensesPerPack(value: string) {
  const [num, _] = value.split(" ") || [];
  return num ? parseFloat(num) : 0;
}

export function buildPairEyewearTopsAddedMessage(
  cartTranslations: Record<string, string> | undefined,
  count: number,
): string | undefined {
  const template =
    count === 1
      ? cartTranslations?.cart_pair_eyewear_tops_added_to_cart_message_single
      : cartTranslations?.cart_pair_eyewear_tops_added_to_cart_message_multiple;

  return template?.replace("{{count}}", String(count));
}

export function parseLensePower(power: string) {
  const [num, sentiment] = power?.split(" ") || [];
  let convertedValue;

  if (sentiment?.toLowerCase() === "negative") {
    convertedValue = -parseFloat(num);
  } else {
    convertedValue = parseFloat(num);
  }

  return convertedValue;
}
